import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_COUNTRY_LIST, GET_LOCATION_LIST } from 'graphql/common/queries'
import { Input, InputGroup } from 'components/input'
import {
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    ArrowLeftIcon,
    UsersIcon,
    PencilIcon,
    EyeIcon,
} from '@heroicons/react/16/solid'
import { t } from 'i18next'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Divider } from 'components/divider'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { useNavigate } from 'react-router-dom'
import { getPageRange } from 'utils/paginationHelper'
import { GET_LUGGAGE_MASTER_PLAN } from 'graphql/luggageMaster/queries'
import { BriefcaseIcon, Cog6ToothIcon, ShoppingBagIcon } from '@heroicons/react/20/solid'
import { Badge } from 'components'
import { CheckInIcon } from 'components/icons'

export default function Luggages() {
    const navigate = useNavigate()
    // Luggage data
    const [searchString, setSearchString] = useState('')
    const [isSearch, setIsSearch] = useState(false)
    const [countries, setCountries] = useState([])

    // Dialog Message

    const [luggageList, setLuggageList] = useState(null)
    const [luggageCount, setLuggageCount] = useState(0)
    const [searchLuggageStatus, setSearchLuggageStatus] = useState('ACT')

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(luggageCount / rowsPerPage)

    const [getLuggageList, { loading: luggageListLoading, error: luggageListError }] = useLazyQuery(
        GET_LUGGAGE_MASTER_PLAN,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchLuggage.success && data.searchLuggage.code === '200') {
                    setLuggageList(data.searchLuggage.luggages)
                    setLuggageCount(data.searchLuggage.total_count)
                }
            },
        }
    )

    // Search luggages List
    const searchluggages = (searchKey, status) => {
        getLuggageList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                search_string: searchKey === '' ? null : searchKey,
                id: null,
            },
        })
    }

    const getCategoryBadge = categoryCode => {
        const categoryMap = {
            AddOn: { color: 'blue', label: 'AddOn' },
            Default: { color: 'purple', label: 'Default' },
        }
        const category = categoryMap[categoryCode] || { color: 'zinc', label: categoryCode }
        return <Badge color={category.color}>{t(category.label)}</Badge>
    }

    const seatOptions = [
        { value: 'STD', description: 'Standard' },
        { value: 'VIP', description: 'VIP' },
    ]
    const seatMap = Object.fromEntries(seatOptions.map(item => [item.value, item.description]))

    const getStatusBadge = luggageStatus => {
        const statusMap = {
            true: { color: 'emerald', label: 'Active' },
            false: { color: 'rose', label: 'Inactive' },
        }
        const status = statusMap[luggageStatus] || { color: 'zinc', label: luggageStatus }
        return <Badge color={status.color}>{t(status.label)}</Badge>
    }

    useEffect(() => {
        searchluggages(searchString, searchLuggageStatus)
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchluggages(searchString, searchLuggageStatus) //
    }
    const onClickSearchClear = () => {
        setSearchString('') // Clear the search string
        setSearchLuggageStatus('All')
        setPage(0)
        setRowsPerPage(10)
        searchluggages('', 'All') // Call searchluggages with an empty string after clearing
        // Let state update before invoking
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <BriefcaseIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3"> {t('Manage luggages')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit luggages.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/luggages/luggages-form')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Luggage')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex-2">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    name="search"
                                    id="search"
                                    placeholder={t('Search luggages')}
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onClickSearch}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {luggageListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {luggageListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch luggages!')}</div>
                </div>
            )}

            {!luggageListLoading && !luggageListError && luggageList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!luggageListLoading && !luggageListError && luggageList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Seat Type')}</TableHeader>
                                    <TableHeader>{t('Luggage type')}</TableHeader>
                                    <TableHeader>{t('Category')}</TableHeader>
                                    <TableHeader>{t('Weight')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {luggageList.map(luggage => (
                                    <TableRow
                                        key={luggage.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/luggages/luggages-form/${luggage.luggage_id}`)}
                                        title={`Luggage #${luggage.id}`}
                                    >
                                        <TableCell>{seatMap[luggage.metadata?.seat_type]}</TableCell>
                                        <TableCell>{getCategoryBadge(luggage.metadata?.luggage_type)}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {luggage.metadata?.category === 'cabin' && (
                                                    <>
                                                        <Cog6ToothIcon className="w-5 h-5 text-primary" />
                                                        <span>Cabin</span>
                                                    </>
                                                )}
                                                {luggage.metadata?.category === 'checkIn' && (
                                                    <>
                                                        <CheckInIcon className="w-5 h-5 text-primary" />
                                                        <span>Check-In</span>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>

                                        <TableCell>{luggage.weight}</TableCell>
                                        <TableCell>{getStatusBadge(luggage.active)}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/luggages/luggages-form/${luggage.luggage_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/luggages/luggages-form/${luggage.luggage_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
        </>
    )
}
